import { useEffect, useRef, useState } from 'react';
import type { IChartApi, MouseEventParams, UTCTimestamp } from 'lightweight-charts';
import { axisMarks, axisBase, dayStride, decodeAxisTime, type AxisMark, type SessionMarket } from '../../lib/indicators';

/**
 * 分时自绘时间轴。
 *
 * 为什么自绘：午休与隔夜都被压缩掉后（见 `axisTime`），横轴是**合成坐标**，
 * lightweight-charts 的自动刻度按时间轴「整点」生成、落点不再对应真实时刻，
 * 因此关掉它的刻度（`ticksVisible:false` + 空 formatter），改由本组件按
 * `axisTime` 的坐标精确绘制 `09:30 … 11:30/13:00 …`（多日则在每天首标日期）。
 *
 * 绝对定位覆盖在图表底部轴条上，`pointer-events:none` 不拦截交互；
 * 跟随缩放/平移（订阅 visibleLogicalRangeChange）与尺寸变化重绘。
 *
 * **未来时段（盘中）的外推**：库的时间轴按数据项等距排布，
 * `timeToCoordinate` 只对 series 里**存在**的数据时间点返回坐标 ——
 * 盘中分时只到当前时刻，11:00/11:30/13:00 等尚未发生的刻度会拿到 null
 * （曾表现为「只剩两端刻度、剩余交易区间看不出格子」）。数据点每分钟等距、
 * 轴对合成秒线性，故从已知刻度求 `pxPerSec` 再外推即可精确补齐未来刻度。
 * 已收盘日数据铺满 → 无 null → 外推退化不发效，两态共用一条路径。
 */
const AXIS_H = 26;
/** 多日模式下每天宽度达到该像素时才补画日内刻度。 */
const INTRADAY_MIN_DAY_W = 360;

interface Props {
  chart: IChartApi | null;
  market: SessionMarket;
  /** MIN1：单日 [date]；MIN5：各交易日日期（升序） */
  dates: string[];
  /** 是否多日（5 日分时） */
  multi: boolean;
}

interface Placed {
  x: number;
  label: string;
  isDate: boolean;
}

export function MinuteTimeAxis({ chart, market, dates, multi }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);
  const [placed, setPlaced] = useState<Placed[]>([]);
  const dateKey = dates.join(',');

  useEffect(() => {
    const el = ref.current;
    if (!chart || !el || dates.length === 0) {
      setPlaced([]);
      return;
    }

    const ts = chart.timeScale();
    const base = axisBase(dates[0]!);
    const stride = dayStride(market);
    const at = (time: number): number | null => {
      const x = ts.timeToCoordinate(time as UTCTimestamp);
      return typeof x === 'number' ? x : null;
    };

    /**
     * 跟随光标的十字线时间徽标。
     * 库的小标签在压缩轴上易被自绘刻度淹没，故由这里用 HTML 绘制，
     * 通过 ref 直接改样式（鼠标移动高频，避免 React 重渲染）。
     */
    const badge = badgeRef.current;
    const onCross = (param: MouseEventParams) => {
      if (!badge) return;
      if (param.time === undefined || param.point === undefined) {
        badge.style.display = 'none';
        return;
      }
      const { dayIndex, hhmm } = decodeAxisTime(Number(param.time), base, market);
      const date = dates[dayIndex];
      badge.textContent = multi && date ? `${date.slice(5)} ${hhmm}` : hhmm;
      badge.style.display = 'block';
      // 夹在可视范围内，避免靠近左右边缘时被 overlay 的 overflow:hidden 裁掉
      const half = badge.offsetWidth / 2;
      const max = el.clientWidth - half;
      badge.style.left = `${Math.max(half, Math.min(max, param.point.x))}px`;
    };
    chart.subscribeCrosshairMove(onCross);

    const build = () => {
      const w = el.clientWidth || 1000;
      const marks: AxisMark[] = [];

      if (!multi) {
        const step = w >= 720 ? 30 : 60;
        marks.push(...axisMarks(base, 0, dates[0]!, market, step, false));
      } else {
        // 多日：每天在其会话首标日期；每天够宽时再补日内刻度
        const starts = dates.map((_, i) => at(base + i * stride));
        const vis = starts
          .map((x, i) => ({ i, x }))
          .filter((s): s is { i: number; x: number } => s.x !== null);
        const dayW = vis.length > 1 ? (vis[vis.length - 1]!.x - vis[0]!.x) / (vis.length - 1) : w;
        const intraday = dayW >= INTRADAY_MIN_DAY_W;
        for (const s of vis) {
          for (const m of axisMarks(base, s.i, dates[s.i]!, market, 60, true)) {
            if (!intraday && !m.isDate) continue;
            marks.push(m);
          }
        }
      }

      /**
       * 第一遍：求可解析刻度；未知（未来时段）者记录待外推。
       */
      const known: { t: number; x: number }[] = [];
      const pending: AxisMark[] = [];
      for (const m of marks) {
        const x = at(m.time);
        if (x !== null) known.push({ t: m.time, x });
        else pending.push(m);
      }
      const placedNow: Placed[] = known.map((k) => {
        const m = marks.find((mm) => mm.time === k.t)!;
        return { x: k.x, label: m.label, isDate: !!m.isDate };
      });

      /**
       * 第二遍：外推未来刻度。从已知的最早/最晚两点求 pxPerSec ——
       * 数据项与合成秒一一等距（每分钟一项），线性严格成立。
       */
      if (known.length >= 2 && pending.length > 0) {
        const k0 = known.reduce((a, b) => (a.t <= b.t ? a : b));
        const k1 = known.reduce((a, b) => (a.t >= b.t ? a : b));
        const pxPerSec = (k1.x - k0.x) / (k1.t - k0.t);
        for (const m of pending) {
          placedNow.push({ x: k1.x + (m.time - k1.t) * pxPerSec, label: m.label, isDate: !!m.isDate });
        }
      }

      placedNow.sort((a, b) => a.x - b.x);
      setPlaced(placedNow);
    };

    /**
     * 隐藏徽标。
     *
     * ⚠️ 两种终止方式都要覆盖：
     *   · 桌面：指针移出图表（mouseleave）
     *   · 触摸：**没有 mouseleave**。手指抬起后 lightweight-charts 不保证
     *     再回调一次 crosshairMove，若只依赖该回调，徽标会停在屏幕上不消失。
     * 故额外监听 pointerup / pointercancel（pointer 事件同时覆盖鼠标与触摸）。
     */
    const hide = () => { if (badge) badge.style.display = 'none'; };
    const host = el.parentElement;
    const release = () => hide();

    build();
    ts.subscribeVisibleLogicalRangeChange(build);
    const ro = new ResizeObserver(build);
    ro.observe(el);
    host?.addEventListener('mouseleave', hide);
    // 用 capture 捕获：手指抬起时事件目标可能是图表内部的 canvas，
    // 不冒泡到 host 就会漏掉。
    host?.addEventListener('pointerup', release, true);
    host?.addEventListener('pointercancel', release, true);
    hide();
    return () => {
      ts.unsubscribeVisibleLogicalRangeChange(build);
      chart.unsubscribeCrosshairMove(onCross);
      ro.disconnect();
      host?.removeEventListener('mouseleave', hide);
      host?.removeEventListener('pointerup', release, true);
      host?.removeEventListener('pointercancel', release, true);
    };
  }, [chart, market, multi, dateKey, dates]);

  return (
    <div
      ref={ref}
      aria-hidden
      className="num"
      style={{
        // 全高覆盖：竖格线要贯穿整个绘图区（库的底部时轴画布为 z-index 1/2
        // 且不透明，覆盖层必须更高才可见）
        position: 'absolute', left: 0, right: 0, top: 0, bottom: 0,
        zIndex: 3,
        pointerEvents: 'none', overflow: 'hidden',
      }}
    >
      {/* 竖格线：把全天会话分成小格。未走完的交易时段同样有线 ——
          剩余区间因这些线而「可见」。日期分隔（多日）用更强的边框色 */}
      {placed.map((p, i) => (
        <div key={`v${i}`} style={{
          position: 'absolute', left: p.x, top: 0,
          width: 1, bottom: AXIS_H,
          background: p.isDate ? 'var(--border-strong)' : 'var(--border)',
        }} />
      ))}

      {/* 底部轴条：刻度小齿 + 时间/日期标签 + 十字线浮动徽标 */}
      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 0, height: AXIS_H,
        color: 'var(--text-3)',
      }}>
        <div
          ref={badgeRef}
          style={{
            position: 'absolute', top: 2, display: 'none', transform: 'translateX(-50%)',
            padding: '1px 5px', borderRadius: 3, fontSize: 11, lineHeight: '14px',
            background: 'var(--text-2)', color: 'var(--bg-surface)', whiteSpace: 'nowrap',
          }}
        />
        {placed.map((p, i) => (
          <div
            key={`${i}:${p.x.toFixed(0)}`}
            style={{
              position: 'absolute', left: p.x, top: 0,
              transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center',
            }}
          >
            <div style={{ width: 1, height: 4, background: 'var(--border)' }} />
            <div
              style={{
                fontSize: 11, lineHeight: '14px', whiteSpace: 'nowrap', marginTop: 1,
                color: p.isDate ? 'var(--text-2)' : 'var(--text-3)',
                fontWeight: p.isDate ? 600 : 400,
              }}
            >
              {p.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
